import type { Environment } from "react-relay";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { apisSignupNewsletterQuery } from "scmp-app/queries/__generated__/apisSignupNewsletterQuery.graphql";
import type {
  apisSignUpNewslettersApiMutation,
  apisSignUpNewslettersApiMutation$data,
  NewsletterSubscriptionsFieldInput,
} from "scmp-app/queries/__generated__/apisSignUpNewslettersApiMutation.graphql";

const query = graphql`
  query apisSignupNewsletterQuery {
    currentUserContext {
      reverseUidSimplenewsSubscriber {
        entities {
          subscriptions {
            status
            entity {
              entityId
            }
          }
        }
      }
    }
  }
`;

export const fetchUserContext = (environment: Environment) => async () => {
  const response = await fetchQuery<apisSignupNewsletterQuery>(
    environment,
    query,
    {},
    {
      networkCacheConfig: {
        force: true,
      },
    },
  ).toPromise();
  return (
    (response?.currentUserContext?.reverseUidSimplenewsSubscriber?.entities
      ?.flatMap(entity =>
        entity?.subscriptions
          ?.filter(subscription => subscription?.status)
          ?.map(subscription => subscription?.entity?.entityId),
      )
      .filter(entity => notEmpty(entity)) as string[]) ?? []
  );
};

const mutation = graphql`
  mutation apisSignUpNewslettersApiMutation($input: NewsletterSubscriberInput) {
    upsertNewsletterSubscriber(input: $input) {
      entity {
        ... on SimplenewsSubscriber {
          uid {
            entity {
              uid
            }
          }
          subscriptions {
            entity {
              entityId
              entityLabel
            }
          }
        }
      }
      errors
    }
  }
`;

export enum NewsletterSignUpAction {
  Subscribe = "1",
  Unsubscribe = "0",
}

export type NewsletterSignUpPayload = {
  action: NewsletterSignUpAction;
  dataMatrix?: Record<string, unknown>;
  email: string;
  environment: Environment;
  newsletterItemIds: string[];
};

export const signUpNewsletters = ({
  action = NewsletterSignUpAction.Subscribe,
  dataMatrix = {},
  email,
  environment,
  newsletterItemIds,
}: NewsletterSignUpPayload) => {
  const handler = () => {
    const signUpInputs = newsletterItemIds.map(
      newsletterItemId =>
        ({
          source: JSON.stringify(dataMatrix),
          status: action,
          targetId: newsletterItemId,
        }) as NewsletterSubscriptionsFieldInput,
    );
    return new Promise<apisSignUpNewslettersApiMutation$data["upsertNewsletterSubscriber"]>(
      resolve => {
        commitMutation<apisSignUpNewslettersApiMutation>(environment, {
          mutation,
          onCompleted: response => {
            resolve(response.upsertNewsletterSubscriber);
          },
          variables: {
            input: {
              append: true,
              mail: email,
              subscriptions: signUpInputs,
            },
          },
        });
      },
    );
  };

  return navigator.locks.request("signUpNewsletters", () => handler()) as ReturnType<
    typeof handler
  >;
};
